import React from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

const JsonViewer = ({ data, type = "single" }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No data
      </Typography>
    );
  }

  const renderValue = (value) => {
    if (value === null) return <em style={{ color: "#999" }}>null</em>;
    if (value === undefined) return <em style={{ color: "#999" }}>undefined</em>;
    if (typeof value === "boolean")
      return (
        <Chip
          label={value ? "True" : "False"}
          size="small"
          color={value ? "success" : "default"}
          sx={{ height: "20px" }}
        />
      );
    if (typeof value === "object") {
      return (
        <pre
          style={{
            fontSize: "11px",
            margin: 0,
            padding: "4px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    return <span>{String(value)}</span>;
  };

  const renderField = (key, value, isBefore = false) => {
    return (
      <Box
        key={key}
        sx={{
          p: 1.5,
          mb: 1,
          borderRadius: 1,
          backgroundColor: isBefore ? "#fff3e0" : "#e8f5e9",
          borderLeft: `3px solid ${isBefore ? "#ff9800" : "#4caf50"}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: "bold",
            color: "text.secondary",
            textTransform: "uppercase",
            fontSize: "10px",
            display: "block",
            mb: 0.5,
          }}
        >
          {key}
        </Typography>
        <Box sx={{ fontSize: "14px" }}>{renderValue(value)}</Box>
      </Box>
    );
  };

  return (
    <Box>
      {Object.entries(data).map(([key, value]) => renderField(key, value))}
    </Box>
  );
};

export const JsonDiffViewer = ({ before, after }) => {
  if (!before && !after) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No changes available
      </Typography>
    );
  }

  const beforeKeys = before ? Object.keys(before) : [];
  const afterKeys = after ? Object.keys(after) : [];
  const allKeys = [...new Set([...beforeKeys, ...afterKeys])].sort();

  const formatFieldName = (key) => {
    // Convert snake_case and camelCase to readable format
    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const renderValue = (value) => {
    if (value === null) return <em style={{ color: "#999" }}>null</em>;
    if (value === undefined) return <em style={{ color: "#999" }}>undefined</em>;
    
    // Handle dates
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={date.toLocaleDateString() + " " + date.toLocaleTimeString()}
                size="small"
                color="info"
                sx={{ height: "22px", fontSize: "11px" }}
              />
            </Box>
          );
        }
      } catch (e) {
        // Not a valid date, continue
      }
    }
    
    // Handle booleans
    if (typeof value === "boolean")
      return (
        <Chip
          label={value ? "True" : "False"}
          size="small"
          color={value ? "success" : "default"}
          sx={{ height: "22px", fontSize: "11px" }}
        />
      );
    
    // Handle numbers
    if (typeof value === "number") {
      return (
        <Typography
          component="span"
          sx={{
            fontWeight: "bold",
            color: "#1976d2",
            fontSize: "14px",
          }}
        >
          {value.toLocaleString()}
        </Typography>
      );
    }
    
    // Handle objects
    if (typeof value === "object" && !Array.isArray(value)) {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <em style={{ color: "#999" }}>Empty object</em>;
      }
      return (
        <Box
          sx={{
            fontSize: "11px",
            backgroundColor: "#f5f5f5",
            p: 1.5,
            borderRadius: 1,
            maxHeight: "150px",
            overflow: "auto",
            border: "1px solid #e0e0e0",
          }}
        >
          {keys.slice(0, 5).map((k) => (
            <Box key={k} sx={{ mb: 0.5 }}>
              <strong>{formatFieldName(k)}:</strong>{" "}
              {typeof value[k] === "object"
                ? JSON.stringify(value[k])
                : String(value[k])}
            </Box>
          ))}
          {keys.length > 5 && (
            <Typography variant="caption" color="text.secondary">
              ... and {keys.length - 5} more fields
            </Typography>
          )}
        </Box>
      );
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <em style={{ color: "#999" }}>Empty array</em>;
      }
      return (
        <Box
          sx={{
            fontSize: "12px",
            backgroundColor: "#f5f5f5",
            p: 1.5,
            borderRadius: 1,
            maxHeight: "150px",
            overflow: "auto",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
            Array ({value.length} items)
          </Typography>
          {value.slice(0, 3).map((item, idx) => (
            <Box key={idx} sx={{ mb: 0.5, pl: 1, borderLeft: "2px solid #1976d2" }}>
              {typeof item === "object" ? JSON.stringify(item) : String(item)}
            </Box>
          ))}
          {value.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              ... and {value.length - 3} more items
            </Typography>
          )}
        </Box>
      );
    }
    
    // Handle strings
    if (typeof value === "string") {
      // Check if it's a long string
      if (value.length > 100) {
        return (
          <Box>
            <Typography
              component="span"
              sx={{ fontSize: "14px", wordBreak: "break-word" }}
            >
              {value.substring(0, 100)}...
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({value.length} chars)
            </Typography>
          </Box>
        );
      }
      return <span style={{ fontSize: "14px", wordBreak: "break-word" }}>{value}</span>;
    }
    
    return <span style={{ fontSize: "14px" }}>{String(value)}</span>;
  };

  const getChangeType = (key) => {
    const beforeVal = before?.[key];
    const afterVal = after?.[key];
    
    if (beforeVal === undefined && afterVal !== undefined) return "added";
    if (beforeVal !== undefined && afterVal === undefined) return "removed";
    if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) return "changed";
    return "unchanged";
  };

  return (
    <Box>
      {allKeys.map((key) => {
        const changeType = getChangeType(key);
        const beforeVal = before?.[key];
        const afterVal = after?.[key];

        if (changeType === "unchanged") return null;

        return (
          <Box
            key={key}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#fafafa",
              border: "1px solid #e0e0e0",
            }}
          >
            {/* Field Name Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                pb: 1,
                borderBottom: "2px solid #e0e0e0",
              }}
            >
              {changeType === "added" && (
                <AddIcon sx={{ color: "#4caf50", fontSize: "18px" }} />
              )}
              {changeType === "removed" && (
                <RemoveIcon sx={{ color: "#f44336", fontSize: "18px" }} />
              )}
              {changeType === "changed" && (
                <EditIcon sx={{ color: "#ff9800", fontSize: "18px" }} />
              )}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "text.primary",
                }}
              >
                {formatFieldName(key)}
              </Typography>
              <Chip
                label={
                  changeType === "added"
                    ? "Added"
                    : changeType === "removed"
                    ? "Removed"
                    : "Changed"
                }
                size="small"
                color={
                  changeType === "added"
                    ? "success"
                    : changeType === "removed"
                    ? "error"
                    : "warning"
                }
                sx={{ height: "20px", fontSize: "10px" }}
              />
            </Box>

            {/* Before/After Values */}
            <Grid container spacing={2}>
              {changeType !== "added" && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "bold",
                      color: "#f44336",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Before:
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: "#ffebee",
                      borderRadius: 1,
                      borderLeft: "3px solid #f44336",
                      minHeight: "40px",
                    }}
                  >
                    {renderValue(beforeVal)}
                  </Box>
                </Grid>
              )}
              {changeType !== "removed" && (
                <Grid item xs={12} md={changeType === "added" ? 12 : 6}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "bold",
                      color: "#4caf50",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    After:
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: "#e8f5e9",
                      borderRadius: 1,
                      borderLeft: "3px solid #4caf50",
                      minHeight: "40px",
                    }}
                  >
                    {renderValue(afterVal)}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default JsonViewer;

